import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, GitMerge, Inbox, MessageSquare, X } from 'lucide-react';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { ApiRequestError } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { useAcceptLead, useDeclineLead, useLeads, useMergeLead } from './api';
import { CAPTURE_CHANNEL_LABELS } from './labels';
import type { Lead } from './types';

/**
 * AmoCRM's "Unsorted" column.
 *
 * Enquiries that arrived on their own — a website chat, a Telegram or
 * WhatsApp message from a stranger — wait here until somebody decides: accept
 * onto the board, decline as spam, or merge into the lead they already are.
 */
export function IncomingColumn({ leads, onOpen }: { leads: Lead[]; onOpen: (lead: Lead) => void }) {
  const accept = useAcceptLead();
  const [declining, setDeclining] = useState<Lead | null>(null);
  const [merging, setMerging] = useState<Lead | null>(null);

  return (
    <div className="flex w-[calc(100%-2.5rem)] shrink-0 snap-start flex-col rounded-xl border-2 border-dashed border-primary/30 bg-primary/[0.03] md:w-72">
      <div className="flex items-center gap-2 border-b border-primary/20 px-3 py-2.5">
        <Inbox className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-primary">Incoming</span>
        <span className="ms-auto rounded-full bg-primary px-2 text-xs font-semibold text-primary-foreground">
          {leads.length}
        </span>
      </div>

      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">
        {leads.map((lead) => (
          <div key={lead.id} className="rounded-lg border bg-background p-2.5 shadow-sm">
            <button onClick={() => onOpen(lead)} className="block w-full text-start">
              <div className="flex items-start justify-between gap-2">
                <span className="line-clamp-1 text-sm font-medium">{lead.name}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(lead.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-[11px] font-medium text-primary">
                {CAPTURE_CHANNEL_LABELS[lead.captureChannel ?? ''] ?? 'Incoming'}
              </p>
              {lead.captureDetails?.firstMessage && (
                <p className="mt-1 line-clamp-2 rounded bg-muted/60 px-2 py-1 text-xs text-muted-foreground">
                  “{String(lead.captureDetails.firstMessage)}”
                </p>
              )}
              {(lead.phone || lead.email) && (
                <p className="mt-1 truncate text-xs text-muted-foreground"><span dir="ltr">{lead.phone ?? lead.email}</span></p>
              )}
            </button>

            <Can permission="crm.lead.update">
              <div className="mt-2 flex items-center gap-1">
                <Button
                  size="sm"
                  className="h-7 flex-1 px-2 text-xs"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate({ id: lead.id })}
                >
                  <Check className="h-3.5 w-3.5" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2"
                  title="Merge into an existing lead"
                  onClick={() => setMerging(lead)}
                >
                  <GitMerge className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-destructive"
                  title="Decline"
                  onClick={() => setDeclining(lead)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                {lead.captureDetails?.conversationId && (
                  <Button asChild size="sm" variant="ghost" className="h-7 px-2" title="Open the conversation">
                    <Link to={`/inbox?c=${String(lead.captureDetails.conversationId)}${lead.captureChannel === 'whatsapp' ? '&source=whatsapp' : ''}`}>
                      <MessageSquare className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                )}
              </div>
            </Can>
          </div>
        ))}
        {leads.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">
            New chats and messages from unknown people land here.
          </p>
        )}
      </div>

      {declining && <DeclineDialog lead={declining} onClose={() => setDeclining(null)} />}
      {merging && <MergeDialog lead={merging} onClose={() => setMerging(null)} />}
    </div>
  );
}

const REASONS = ['Spam', 'Wrong number', 'Job seeker', 'Duplicate', 'Not our service'];

function DeclineDialog({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const decline = useDeclineLead();
  const [reason, setReason] = useState('Spam');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decline {lead.name}?</DialogTitle>
          <DialogDescription>It leaves the Incoming column and never reaches the board or the figures.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {REASONS.map((item) => (
            <button
              key={item}
              onClick={() => setReason(item)}
              className={`rounded-full border px-3 py-1 text-xs ${reason === item ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              {item}
            </button>
          ))}
        </div>
        <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason" />
        {decline.error && <FormError message={(decline.error as ApiRequestError).message} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={decline.isPending}
            onClick={() => decline.mutate({ id: lead.id, reason }, { onSuccess: onClose })}
          >
            Decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const merge = useMergeLead();
  const [search, setSearch] = useState(lead.phone ?? lead.email ?? lead.firstName);
  const debounced = useDebounce(search, 300);
  const { data } = useLeads({ q: debounced || undefined, limit: 8 });
  const candidates = (data?.data ?? []).filter((candidate) => candidate.id !== lead.id);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge {lead.name} into an existing lead</DialogTitle>
          <DialogDescription>
            The same person writing again from another channel. Their conversations move across, so the history
            sits on one record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Find the lead</Label>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, phone, email…" />
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {candidates.map((candidate) => (
            <button
              key={candidate.id}
              disabled={merge.isPending}
              onClick={() => merge.mutate({ id: lead.id, targetLeadId: candidate.id }, { onSuccess: onClose })}
              className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-start text-sm hover:bg-muted"
            >
              <span>
                <span className="font-medium">{candidate.name}</span>
                <span className="ms-2 text-xs text-muted-foreground">{candidate.phone ?? candidate.email ?? ''}</span>
              </span>
              <span className="text-xs text-muted-foreground">{candidate.number}</span>
            </button>
          ))}
          {candidates.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No matching leads.</p>}
        </div>
        {merge.error && <FormError message={(merge.error as ApiRequestError).message} />}
      </DialogContent>
    </Dialog>
  );
}
